import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { EmailIcon, MealIcon, PenIcon, SendIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { Skeleton } from '@repo/ui/skeleton'
import TextEditor from '@repo/ui/text-editor'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  cancelTransferMealOrder,
  getTransferMealOrder,
  resendTransferMealOrder
} from '../../../api/transfers/transfer-meal-orders'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../main'
import TripFolderOrderMealForm from './trip-folder-order-meal-form'

interface Props {
  isOpen: boolean
  folderId: number
  orderId: number
  onClose: () => void
}

const TripFolderViewTransferMeal = ({ isOpen, onClose, folderId, orderId }: Props) => {
  const [mode, setMode] = useState<'view' | 'edit' | 'cancel'>('view')

  const { data: order, isPending } = useQuery({
    queryKey: ['transfer-meal-order', orderId],
    queryFn: () => getTransferMealOrder(orderId),
    enabled: isOpen
  })

  const onSuccess = ({ message }: { message: string }) => {
    toast.success(message)
    queryClient.invalidateQueries({ queryKey: ['transfer-meal-order', orderId] })
    queryClient.invalidateQueries({ queryKey: ['trip-folder-transfers', folderId] })
    setMode('view')
  }

  const { mutate: resend, isPending: isResendPending } = useMutation({
    mutationFn: resendTransferMealOrder,
    onSuccess,
    onError: showAxiosError
  })

  const { mutate: cancel, isPending: isCancelPending } = useMutation({
    mutationFn: cancelTransferMealOrder,
    onSuccess,
    onError: showAxiosError
  })

  if (mode === 'cancel')
    return (
      <Modal
        isOpen={isOpen}
        title="Êtes-vous sûr de vouloir annuler cette commande de repas ? Le prestataire en sera informé par email."
        icon={MealIcon}
        size="small"
        variant="confirmation"
        isLoading={isCancelPending}
        onCancel={() => setMode('view')}
        actionButtonsOptions={{
          cancelText: 'Retour',
          confirmText: 'Annuler la commande',
          onConfirm: () => cancel(orderId)
        }}
      />
    )

  if (mode === 'edit' && order)
    return (
      <Modal
        isOpen={isOpen}
        title="Modifier la commande de repas"
        icon={MealIcon}
        size="medium"
        hideActions
        onCancel={() => setMode('view')}
      >
        <TripFolderOrderMealForm
          folderId={folderId}
          order={order}
          toggleModal={() => setMode('view')}
        />
      </Modal>
    )

  return (
    <Modal
      isOpen={isOpen}
      title="Détails de la commande de repas"
      icon={EmailIcon}
      size="medium"
      hideActions
      onCancel={onClose}
    >
      <div className={MODAL_BODY_CLASSNAME}>
        {isPending || !order ? (
          <Skeleton className="h-[300px] w-full" />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-[120px_1fr] gap-y-4">
              <Typography variant="label-semibold" color="grey">
                Expéditeur :
              </Typography>
              <Typography variant="label-semibold">
                {StringFormatter.capitalize(order.createdBy.firstName)}{' '}
                {order.createdBy.lastName.toUpperCase()}
              </Typography>

              <Typography variant="label-semibold" color="grey">
                Prestataire :
              </Typography>
              <Typography variant="label-semibold">{order.tripProvider.companyName}</Typography>

              <Typography variant="label-semibold" color="grey">
                Destinataire :
              </Typography>
              <div className="flex flex-wrap gap-2">
                {order.contacts.length > 0
                  ? order.contacts.map((contact, index) => (
                      <Badge
                        variant="grey"
                        text={contact}
                        key={index}
                        className="px-2 py-1 h-fit"
                      />
                    ))
                  : '-'}
              </div>

              <Typography variant="label-semibold" color="grey">
                Envoyée le :
              </Typography>
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant="grey"
                  text={DateFormatter.DDMMYYYY(order.sentAt)}
                  className="px-2 py-1 h-fit"
                />
                {order.cancelledAt && (
                  <Badge
                    variant="danger"
                    text={`Annulée le ${DateFormatter.DDMMYYYY(order.cancelledAt)}`}
                    className="px-2 py-1 h-fit"
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Typography variant="label-semibold">Répartition des repas</Typography>
              {order.lines.map((line) => (
                <div
                  key={line.transferPlaceId}
                  className="flex items-center gap-4 rounded-xl border border-grey-light p-3"
                >
                  <div className="flex flex-1 flex-col gap-1">
                    <Typography variant="label-semibold">
                      {line.date} - {line.time}
                    </Typography>
                    <Typography variant="secondary-body">{line.address}</Typography>
                  </div>
                  <Badge
                    variant="primary"
                    text={`${line.numberOfMeals} repas pour ${line.station}`}
                  />
                </div>
              ))}
            </div>

            {order.description && (
              <TextEditor value={order.description} readOnly={true} hideControls />
            )}

            {!order.cancelledAt && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="secondary"
                  label="Annuler la commande"
                  onClick={() => setMode('cancel')}
                />
                <Button
                  variant="secondary"
                  label="Renvoyer"
                  icon={SendIcon}
                  loading={isResendPending}
                  onClick={() => resend(orderId)}
                />
                <Button
                  variant="primary"
                  label="Modifier"
                  icon={PenIcon}
                  onClick={() => setMode('edit')}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default TripFolderViewTransferMeal
